import {
	Box,
	Button,
	Flex,
	Heading,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Textarea,
	VStack,
} from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { PDF_TEMPLATE_VARS, PdfTemplateVar } from "../../configs/constants/pdfTemplateVars";
import { PDF_TEMPLATE_PREVIEW_CONTEXT } from "../../configs/constants/pdfTemplateFixture";
import { useUpdateOrganisationNotificationTemplate } from "../../networking/organisation.hooks";
import { NotificationTemplate } from "../../models/OrgNotificationSetting";

interface PdfTemplateCardProps {
	template: NotificationTemplate;
	onEdit: () => void;
}

export const PdfTemplate: React.FC<PdfTemplateCardProps> = ({ template, onEdit }) => {
	const { t } = useTranslation();
	const previewHtml = useMemo(() => buildPreviewDoc(template.body || ""), [template.body]);

	return (
		<Box w="full" py="2" px="3" bg="white" fontFamily={"mono"} fontSize={"sm"}>
			<Flex justifyContent={"flex-end"} gap="2">
				<Button
					colorScheme="blue"
					variant={"outline"}
					onClick={onEdit}
					size="xs"
					alignSelf={"flex-end"}
					mb="2"
				>
					{t("organisation.notifications.editTemplate")}
				</Button>
			</Flex>
			{!template.body && (
				<Box p="3" bg="orange.50" borderRadius="md">
					<Text fontSize="sm" color="orange.700">
						Ingen HTML-mall är konfigurerad. Klicka på "Redigera mall" för att lägga till.
					</Text>
				</Box>
			)}
			{template.body && (
				<Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden" mt="2">
					<iframe
						title={`pdf-preview-${template.name}`}
						srcDoc={previewHtml}
						sandbox="allow-scripts"
						style={{ width: "100%", height: "400px", border: "none", background: "white" }}
					/>
				</Box>
			)}
		</Box>
	);
};

interface EditPdfTemplateModalProps {
	template?: NotificationTemplate;
	onClose: () => void;
}

export const EditPdfTemplateModal: React.FC<EditPdfTemplateModalProps> = ({
	template,
	onClose,
}) => {
	const { mutate, isLoading } = useUpdateOrganisationNotificationTemplate();
	const { t } = useTranslation();
	const ref = useRef<HTMLTextAreaElement>(null);
	const [draft, setDraft] = useState(template?.body || "");

	React.useEffect(() => {
		setDraft(template?.body || "");
	}, [template?.id]);

	const previewHtml = useMemo(() => buildPreviewDoc(draft), [draft]);

	const vars: PdfTemplateVar[] = template ? PDF_TEMPLATE_VARS[template.name] || [] : [];

	async function handleSubmit() {
		if (template) {
			await mutate({
				id: template.id,
				body: ref.current?.value || "",
			});
		}
		onClose();
	}

	return (
		<Modal isOpen={!!template} size="6xl" onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalHeader>{template?.humanReadableName || template?.name}</ModalHeader>
				<ModalCloseButton />
				<ModalBody>
					<Flex gap="4" alignItems="stretch" flexDirection={{ base: "column", lg: "row" }}>
						<VStack flex="1" spacing="3" alignItems="stretch">
							<Heading fontSize="sm">HTML-mall</Heading>
							<Textarea
								h={"500px"}
								fontFamily={"mono"}
								fontSize={"sm"}
								defaultValue={draft}
								onChange={(e) => setDraft(e.target.value)}
								ref={ref}
							/>
							<Text fontSize="xs" color="gray.600">
								PDF:en renderas i A4-format. Använd inline-CSS för bästa resultat. Datumformatering:{" "}
								<code>{`{{moment job.startDate "YYYY-MM-DD"}}`}</code>. Listor:{" "}
								<code>{`{{#each shiftsDisplay}}...{{/each}}`}</code>.
							</Text>
						</VStack>
						<VStack flex="1" spacing="3" alignItems="stretch">
							<Heading fontSize="sm">Förhandsgranskning</Heading>
							<Box borderWidth="1px" borderColor="gray.200" borderRadius="md" overflow="hidden">
								<iframe
									title="pdf-preview"
									srcDoc={previewHtml}
									sandbox="allow-scripts"
									style={{ width: "100%", height: "500px", border: "none", background: "white" }}
								/>
							</Box>
							{vars.length > 0 && (
								<Box>
									<Heading fontSize="sm" mb="2">
										Tillgängliga variabler
									</Heading>
									<Box maxH="200px" overflowY="auto" borderWidth="1px" borderRadius="md" p="2">
										{vars.map((v) => (
											<Box key={v.name} mb="2">
												<Text fontSize="xs" fontFamily="mono" fontWeight="bold">
													{v.raw ? `{{{${v.name}}}}` : `{{${v.name}}}`}
												</Text>
												<Text fontSize="xs" color="gray.600">
													{v.description}
												</Text>
											</Box>
										))}
									</Box>
								</Box>
							)}
						</VStack>
					</Flex>
				</ModalBody>

				<ModalFooter>
					<Button
						colorScheme="green"
						mr={3}
						onClick={handleSubmit}
						isLoading={isLoading}
						loadingText={t("LOADING") + "..."}
					>
						{t("SAVE")}
					</Button>
					<Button variant="ghost" onClick={onClose}>
						{t("CANCEL")}
					</Button>
				</ModalFooter>
			</ModalContent>
		</Modal>
	);
};

/**
 * Builds an isolated HTML document that loads Handlebars (and moment, for the
 * `moment` helper backed templates use) from a CDN and compiles the user's
 * template against the static preview fixture entirely inside the iframe.
 *
 * Done this way because bundling Handlebars through Vite/Rollup mangles the
 * compiler's internal `parse` symbol in production builds — running it inside
 * an iframe with a CDN script tag sidesteps the bundler entirely.
 */
function buildPreviewDoc(templateSource: string): string {
	if (!templateSource) return "";

	const safeTemplate = JSON.stringify(templateSource);
	const safeContext = JSON.stringify(PDF_TEMPLATE_PREVIEW_CONTEXT);

	return `<!doctype html>
<html>
<head>
	<meta charset="utf-8" />
	<script src="https://cdn.jsdelivr.net/npm/handlebars@4.7.8/dist/handlebars.min.js"></script>
	<script src="https://cdn.jsdelivr.net/npm/moment@2.29.4/min/moment.min.js"></script>
</head>
<body>
	<div id="root"></div>
	<script>
		(function () {
			if (typeof Handlebars === "undefined") {
				document.getElementById("root").innerHTML =
					'<pre style="color:#c00;font-family:monospace;padding:1rem;">Kunde inte ladda Handlebars från CDN.</pre>';
				return;
			}

			Handlebars.registerHelper("moment", function (date, format) {
				if (!date) return "";
				var fmt = typeof format === "string" ? format : "YYYY-MM-DD";
				if (typeof moment !== "undefined") return moment(date).format(fmt);
				try { return new Date(date).toISOString().slice(0, 10); } catch (_) { return String(date); }
			});

			try {
				var template = Handlebars.compile(${safeTemplate});
				document.getElementById("root").innerHTML = template(${safeContext});
			} catch (err) {
				document.getElementById("root").innerHTML =
					'<pre style="color:#c00;font-family:monospace;padding:1rem;">Mall-fel: ' +
					String(err && err.message ? err.message : err).replace(/</g, "&lt;") +
					'</pre>';
			}
		})();
	</script>
</body>
</html>`;
}
